import { deflateSync, inflateSync, strFromU8, strToU8 } from 'fflate';
import { validateTicketGraph } from '../domain/merge';
import type {
  CoupleSpace,
  EventAction,
  Importance,
  MemberId,
  Ticket,
  TicketEvent,
  TicketState,
  TicketType,
} from '../domain/types';
import { fromBase64Url } from './base64url';
import { ProtocolError } from './crypto';

export type MessagePayload =
  | { kind: 'ticket'; ticket: Ticket }
  | {
    kind: 'pairing-receipt'; coupleId: string; memberId: MemberId;
    memberName?: string; memberAvatar?: string; at: string;
  };

export interface InvitePayload {
  coupleId: string;
  spaceName: string;
  inviterName: string;
  inviterAvatar: string;
  anniversary: string | null;
  key: string;
}

interface CompactEvent {
  i: string;
  p: string[];
  m: number;
  a: number;
  t: string;
  n?: string;
  s: number;
}

interface CompactTicket {
  i: string;
  c: string;
  f: number;
  r: number;
  y: number;
  t: string;
  d: string;
  o: string;
  u: string | null;
  q: number;
  g: string[];
  s: string;
  x: null;
  a: string;
  z: string;
  w: number;
  h: string;
  e: CompactEvent[];
}

interface WireMessageV1 {
  v: 1;
  k: 't' | 'p';
  t?: CompactTicket;
  c?: string;
  m?: MemberId;
  n?: string;
  x?: string;
  a?: string;
}

interface WireInviteV1 {
  v: 1;
  c: string;
  n: string;
  a: string;
  x?: string;
  d: string | null;
  k: string;
}

const MEMBER_IDS = ['creator', 'partner'] as const satisfies readonly MemberId[];
const TICKET_TYPES = ['reward', 'penalty'] as const satisfies readonly TicketType[];
const IMPORTANCE_VALUES = ['small', 'promise', 'important'] as const satisfies readonly Importance[];
const TICKET_STATES = ['pending_response', 'accepted', 'rejected', 'pending_confirmation', 'completed'] as const satisfies readonly TicketState[];
const EVENT_ACTIONS = ['created', 'accepted', 'rejected', 'submitted', 'confirmed', 'conflict_resolved'] as const satisfies readonly EventAction[];

export function encodeWireMessage(message: MessagePayload): Uint8Array {
  const wire: WireMessageV1 = message.kind === 'ticket'
    ? { v: 1, k: 't', t: compactTicket(message.ticket) }
    : {
      v: 1, k: 'p', c: message.coupleId, m: message.memberId,
      ...(message.memberName === undefined ? {} : { n: message.memberName }),
      ...(message.memberAvatar === undefined ? {} : { x: message.memberAvatar }),
      a: message.at,
    };
  return strToU8(JSON.stringify(wire));
}

export function decodeWireMessage(bytes: Uint8Array): MessagePayload {
  const wire = parseWire(bytes);
  assertVersion(wire);

  if (wire.k === 't') {
    const ticket = expandTicket(requireRecord(wire.t));
    try {
      validateTicketGraph(ticket);
    } catch {
      throw new ProtocolError('链接内容格式无效', 'INVALID_TICKET');
    }
    return { kind: 'ticket', ticket };
  }
  if (wire.k === 'p') {
    return {
      kind: 'pairing-receipt',
      coupleId: requireString(wire.c),
      memberId: requireLiteral(wire.m, MEMBER_IDS),
      ...(wire.n === undefined ? {} : { memberName: requireString(wire.n) }),
      ...(wire.x === undefined ? {} : { memberAvatar: requireString(wire.x) }),
      at: requireString(wire.a),
    };
  }
  throw invalidFormat();
}

export function encodeWireInvite(space: CoupleSpace): Uint8Array {
  const wire: WireInviteV1 = {
    v: 1,
    c: space.coupleId,
    n: space.spaceName,
    a: space.selfName,
    x: space.selfAvatar,
    d: space.anniversary,
    k: space.key,
  };
  validateInvite(wire);
  return strToU8(JSON.stringify(wire));
}

export function decodeWireInvite(bytes: Uint8Array): InvitePayload {
  const wire = parseWire(bytes);
  assertVersion(wire);
  const invite: InvitePayload = {
    coupleId: requireString(wire.c),
    spaceName: requireString(wire.n),
    inviterName: requireString(wire.a),
    inviterAvatar: wire.x === undefined ? '🐰' : requireString(wire.x),
    anniversary: requireNullableString(wire.d),
    key: requireString(wire.k),
  };
  validateInvite({
    v: 1, c: invite.coupleId, n: invite.spaceName, a: invite.inviterName,
    x: invite.inviterAvatar, d: invite.anniversary, k: invite.key,
  });
  return invite;
}

export function compressWire(bytes: Uint8Array): Uint8Array {
  return deflateSync(bytes, { level: 6 });
}

export function decompressWire(bytes: Uint8Array): Uint8Array {
  try {
    return inflateSync(bytes);
  } catch {
    throw invalidFormat();
  }
}

function compactTicket(ticket: Ticket): CompactTicket {
  return {
    i: ticket.id,
    c: ticket.coupleId,
    f: encodeEnum(ticket.creatorId, MEMBER_IDS),
    r: encodeEnum(ticket.recipientId, MEMBER_IDS),
    y: encodeEnum(ticket.type, TICKET_TYPES),
    t: ticket.title,
    d: ticket.description,
    o: ticket.outcome,
    u: ticket.dueDate,
    q: encodeEnum(ticket.importance, IMPORTANCE_VALUES),
    g: [...ticket.tags],
    s: ticket.signature,
    x: null,
    a: ticket.createdAt,
    z: ticket.updatedAt,
    w: encodeEnum(ticket.state, TICKET_STATES),
    h: ticket.headEventId,
    e: ticket.events.map(compactEvent),
  };
}

function compactEvent(event: TicketEvent): CompactEvent {
  return {
    i: event.id,
    p: [...event.parentIds],
    m: encodeEnum(event.actor, MEMBER_IDS),
    a: encodeEnum(event.action, EVENT_ACTIONS),
    t: event.at,
    ...(event.note === undefined ? {} : { n: event.note }),
    s: encodeEnum(event.state, TICKET_STATES),
  };
}

function expandTicket(value: Record<string, unknown>): Ticket {
  return {
    id: requireString(value.i),
    coupleId: requireString(value.c),
    creatorId: decodeEnum(value.f, MEMBER_IDS),
    recipientId: decodeEnum(value.r, MEMBER_IDS),
    type: decodeEnum(value.y, TICKET_TYPES),
    title: requireString(value.t, true),
    description: requireString(value.d, true),
    outcome: requireString(value.o, true),
    dueDate: requireNullableString(value.u),
    importance: decodeEnum(value.q, IMPORTANCE_VALUES),
    tags: requireStringArray(value.g),
    signature: requireString(value.s, true),
    imageId: requireNull(value.x),
    createdAt: requireString(value.a),
    updatedAt: requireString(value.z),
    state: decodeEnum(value.w, TICKET_STATES),
    headEventId: requireString(value.h),
    events: requireArray(value.e).map((event) => expandEvent(requireRecord(event))),
  };
}

function expandEvent(value: Record<string, unknown>): TicketEvent {
  return {
    id: requireString(value.i),
    parentIds: requireStringArray(value.p),
    actor: decodeEnum(value.m, MEMBER_IDS),
    action: decodeEnum(value.a, EVENT_ACTIONS),
    at: requireString(value.t),
    ...(value.n === undefined ? {} : { note: requireString(value.n, true) }),
    state: decodeEnum(value.s, TICKET_STATES),
  };
}

function parseWire(bytes: Uint8Array): Record<string, unknown> {
  try {
    return requireRecord(JSON.parse(strFromU8(bytes)));
  } catch (error) {
    if (error instanceof ProtocolError) throw error;
    throw invalidFormat();
  }
}

function assertVersion(wire: Record<string, unknown>): void {
  if (wire.v !== 1) throw new ProtocolError('不支持的链接版本', 'UNSUPPORTED_VERSION');
}

function validateInvite(invite: WireInviteV1): void {
  if (!invite.c || !invite.n || !invite.a || (invite.x !== undefined && !invite.x) || fromBase64Url(invite.k).length !== 32) {
    throw invalidFormat();
  }
}

function encodeEnum<T extends string>(value: T, table: readonly T[]): number {
  const index = table.indexOf(value);
  if (index < 0) throw invalidFormat();
  return index;
}

function decodeEnum<T extends string>(value: unknown, table: readonly T[]): T {
  if (!Number.isInteger(value) || typeof value !== 'number' || value < 0 || value >= table.length) throw invalidFormat();
  return table[value]!;
}

function requireLiteral<T extends string>(value: unknown, choices: readonly T[]): T {
  if (typeof value !== 'string' || !choices.includes(value as T)) throw invalidFormat();
  return value as T;
}

function requireRecord(value: unknown): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) throw invalidFormat();
  return value as Record<string, unknown>;
}

function requireArray(value: unknown): unknown[] {
  if (!Array.isArray(value)) throw invalidFormat();
  return value;
}

function requireString(value: unknown, allowEmpty = false): string {
  if (typeof value !== 'string' || (!allowEmpty && value.length === 0)) throw invalidFormat();
  return value;
}

function requireNullableString(value: unknown): string | null {
  if (value === null) return null;
  return requireString(value);
}

function requireStringArray(value: unknown): string[] {
  if (!Array.isArray(value) || value.some((item) => typeof item !== 'string')) throw invalidFormat();
  return value as string[];
}

function requireNull(value: unknown): null {
  if (value !== null) throw invalidFormat();
  return null;
}

function invalidFormat(): ProtocolError {
  return new ProtocolError('链接内容格式无效', 'MALFORMED_PAYLOAD');
}
